// src/modules/assetManagement/components/EventProductPicker.tsx
// Event + Product picker that follows the linking rule: pick an Event to see its Products,
// or pick a Product to see the Events it belongs to (a single Event is picked automatically).
import React, { useState } from "react";
import { AlertTriangle, CalendarDays, Package } from "lucide-react";
import type { LibraryProduct } from "../types";
import {
  eventsForProduct,
  toggleEvent,
  toggleProduct,
  uncoveredProducts,
  visibleOptions,
  type EventLike,
  type LinkSelection,
} from "../linkRules";
import { ENTITY } from "../utils";
import { SearchInput } from "./ui";

interface Row {
  id: string;
  label: string;
  meta?: string;
  locked?: boolean;
}

const List: React.FC<{
  title: string;
  icon: React.ReactNode;
  accent: string;
  rows: Row[];
  selected: string[];
  onToggle: (id: string) => void;
  note?: string;
  emptyText: string;
  compact?: boolean;
}> = ({ title, icon, accent, rows, selected, onToggle, note, emptyText, compact }) => {
  const [query, setQuery] = useState("");
  const shown = rows.filter((r) => r.label.toLowerCase().includes(query.trim().toLowerCase()));
  const count = selected.length;
  return (
    <div className="flex flex-col min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <span style={{ color: accent }}>{icon}</span>
        <span className={`font-semibold text-slate-800 ${compact ? "text-xs uppercase tracking-wide" : "text-sm"}`}>{title}</span>
        {count > 0 && <span className="text-xs text-slate-500">· {count} selected</span>}
      </div>
      {note && <p className="text-[11px] text-slate-500 mb-1.5">{note}</p>}
      {!compact && rows.length > 6 && (
        <SearchInput value={query} onChange={setQuery} placeholder={`Search ${title.toLowerCase()}`} className="w-full mb-2" />
      )}
      <div className={`border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-y-auto ${compact ? "max-h-40" : "max-h-64"}`}>
        {shown.length === 0 && <p className="text-sm text-slate-500 p-3 text-center">{emptyText}</p>}
        {shown.map((r) => (
          <label
            key={r.id}
            className={`flex items-center gap-3 ${compact ? "px-3 py-1.5" : "px-3 py-2.5"} text-sm ${
              r.locked ? "bg-slate-50 cursor-default" : "cursor-pointer hover:bg-slate-50"
            }`}
          >
            <input
              type="checkbox"
              className="h-4 w-4 accent-slate-700 shrink-0"
              checked={r.locked || selected.includes(r.id)}
              disabled={r.locked}
              onChange={() => onToggle(r.id)}
            />
            <span className="flex-1 min-w-0">
              <span className="block truncate text-slate-800">{r.label}</span>
              {r.meta && <span className="block text-xs text-slate-500 truncate">{r.meta}</span>}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

const EventProductPicker: React.FC<{
  events: EventLike[];
  products: LibraryProduct[];
  value: LinkSelection;
  onChange: (next: LinkSelection) => void;
  /** Events every target asset is already linked to: they cover products too */
  lockedEventIds?: string[];
  lockedProductIds?: string[];
  eventMeta?: (id: string) => string | undefined;
  productMeta?: (id: string) => string | undefined;
  layout?: "columns" | "stacked";
}> = ({
  events,
  products,
  value,
  onChange,
  lockedEventIds = [],
  lockedProductIds = [],
  eventMeta,
  productMeta,
  layout = "columns",
}) => {
  const compact = layout === "stacked";
  const visible = visibleOptions(events, products.map((p) => p.id), value, lockedEventIds);
  const missing = uncoveredProducts(events, value, lockedEventIds);
  const titleOf = (id: string) => products.find((p) => p.id === id)?.title ?? id;

  const eventRows: Row[] = events
    .filter((e) => visible.eventIds.includes(e.id) || lockedEventIds.includes(e.id))
    .map((e) => ({
      id: e.id,
      label: e.title,
      meta: eventMeta?.(e.id) ?? (e.productIds.length ? `${e.productIds.length} product${e.productIds.length === 1 ? "" : "s"}` : "No products"),
      locked: lockedEventIds.includes(e.id),
    }));

  const productRows: Row[] = products
    .filter((p) => visible.productIds.includes(p.id) || lockedProductIds.includes(p.id))
    .map((p) => {
      const inEvents = eventsForProduct(events, p.id).map((e) => e.title);
      return {
        id: p.id,
        label: p.title,
        meta: productMeta?.(p.id) ?? (inEvents.length ? `In ${inEvents.join(", ")}` : undefined),
        locked: lockedProductIds.includes(p.id),
      };
    });

  return (
    <div className="space-y-3">
      <div className={layout === "columns" ? "grid grid-cols-1 md:grid-cols-2 gap-5" : "space-y-4"}>
        <List
          title="Events"
          icon={<CalendarDays size={compact ? 13 : 16} />}
          accent={ENTITY.event.solid}
          rows={eventRows}
          selected={value.eventIds}
          onToggle={(id) => onChange(toggleEvent(events, value, id, lockedEventIds))}
          note={visible.filteredByProduct ? "Showing the events your selected products belong to" : undefined}
          emptyText="No events"
          compact={compact}
        />
        <List
          title="Products"
          icon={<Package size={compact ? 13 : 16} />}
          accent={ENTITY.product.solid}
          rows={productRows}
          selected={value.productIds}
          onToggle={(id) => onChange(toggleProduct(events, value, id, lockedEventIds))}
          note={visible.filteredByEvent ? "Showing products in the selected events" : "Pick a product and its event is added for you"}
          emptyText={visible.filteredByEvent ? "The selected events have no products" : "No products are linked to an event yet"}
          compact={compact}
        />
      </div>
      {missing.length > 0 && (
        <p className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>
            Choose which event {missing.length === 1 ? "this product is" : "these products are"} for:{" "}
            <span className="font-medium">{missing.map(titleOf).join(", ")}</span>
          </span>
        </p>
      )}
    </div>
  );
};

export default EventProductPicker;
